/**
 * fichaPersistence — autosave del editor de notas clínicas (UX_VISION P1-9).
 *
 * `sessionStorage`, NO `localStorage`: son campos de datos de SALUD de un
 * tercero (el paciente) en el navegador del terapeuta, que puede ser un PC de
 * clínica compartido. No debe sobrevivir al cierre del navegador ni al logout.
 *
 * Revisado por `security` antes de implementar (engram `sec/p1-9-ficha-autosave-review`,
 * "OK con cambios"). Difiere de P1-5 ([[bookingPersistence]]):
 *  - el dato es de un TERCERO que no lo tipeó → interés legítimo del responsable, no consentimiento
 *  - PC compartido entre terapeutas y con el director
 *  - EXISTE backend (RPC `update_session_clinical_notes`): el draft de cliente solo cubre
 *    "se cayó la señal / cerré sin querer", el commit real va al server
 *
 * Endurecimientos sobre P1-5:
 *  - scope por `ownerAuthUserId` (JWT sub) — mismatch al leer = descartar + borrar clave
 *  - wipe de TODOS los drafts en logout (`handleLogout` + `onAuthStateChange`) — equivale a SEC-043
 *  - TTL sliding 60 min + TTL absoluto 8 h
 *  - baseline desde re-fetch fresco del server al abrir (lo hace el consumidor), no de props stale
 *  - banner sin preview de valores, SOLO en el editor de esa `consulta_id`
 */

import type { EditableSessionFields } from '../../lib/clinicalRecords/types';

const SCHEMA_VERSION = 1;
const KEY_PREFIX = 'cc:ficha:draft:';
const TTL_SLIDING_MS = 60 * 60_000;      // 60 min desde la última escritura
const TTL_ABSOLUTE_MS = 8 * 60 * 60_000; // 8 h desde que se creó el draft

/**
 * Los 7 campos editables del editor reducido: 3 catálogos ("Encabezado
 * clínico") + 4 observaciones ("Registro clínico de la sesión"). Los demás
 * params de `update_session_clinical_notes` se siguen mandando pero como
 * `null` desde el caller (COALESCE preserva lo previo).
 */
export const EDITABLE_FIELD_KEYS: (keyof EditableSessionFields)[] = [
  'motivo_categoria',
  'ciclo_terapeutico',
  'ciclo_vital_etapa',
  'observacion_clinica',      // Observación I
  'desarrollo_sesion',        // Observación II
  'respuesta_evolucion',      // Observación III
  'observaciones_adicionales', // Observación IV (opcional)
];

/** Catálogos: la RPC trata `''` como "no tocar" (fix 2026-08-21) — el dirty-check lo espeja. */
const CATALOG_KEYS: (keyof EditableSessionFields)[] = [
  'motivo_categoria',
  'ciclo_terapeutico',
  'ciclo_vital_etapa',
];

/** Las 4 observaciones (texto libre) — su contenido define si un draft "vale la pena". */
const FREE_TEXT_KEYS = EDITABLE_FIELD_KEYS.filter((k) => !CATALOG_KEYS.includes(k));

export type FichaFields = Record<keyof EditableSessionFields, string>;

export interface FichaDraft {
  fields: FichaFields;
  /** Snapshot de los campos editables del server cuando se abrió el editor. */
  baseline: FichaFields;
  ownerAuthUserId: string;
  createdAt: number;
  savedAt: number;
}

interface DraftBlob extends FichaDraft {
  v: number;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && !!window.sessionStorage;
}

function keyFor(consultaId: string): string {
  return KEY_PREFIX + consultaId;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** Normaliza cualquier fuente (props del server, blob del storage) a los strings editables. */
export function fieldsFrom(source: unknown): FichaFields {
  const s = (source && typeof source === 'object' ? source : {}) as Record<string, unknown>;
  const out = {} as FichaFields;
  for (const k of EDITABLE_FIELD_KEYS) out[k] = str(s[k]);
  return out;
}

/** Blob del storage = input NO confiable: valida forma + tipos. */
function sanitizeFields(raw: unknown): FichaFields | null {
  if (!raw || typeof raw !== 'object') return null;
  return fieldsFrom(raw as Record<string, unknown>);
}

/** Igualdad de campos con `NULLIF(x,'')` implícito (trim + '' ≡ ausente). */
export function fieldsEqual(a: Partial<FichaFields>, b: Partial<FichaFields>): boolean {
  return EDITABLE_FIELD_KEYS.every((k) => str(a[k]).trim() === str(b[k]).trim());
}

/** ¿El draft difiere del server de forma que valga la pena ofrecer recuperarlo? */
export function draftHasMeaningfulEdits(fields: FichaFields, serverFields: FichaFields): boolean {
  const freeTextChanged = FREE_TEXT_KEYS.some(
    (k) => str(fields[k]).trim() && str(fields[k]).trim() !== str(serverFields[k]).trim()
  );
  const catalogChanged = CATALOG_KEYS.some(
    (k) => str(fields[k]).trim() !== str(serverFields[k]).trim()
  );
  return freeTextChanged || catalogChanged;
}

export function loadDraft(consultaId: string, ownerAuthUserId: string): FichaDraft | null {
  if (!isBrowser() || !consultaId || !ownerAuthUserId) return null;
  const key = keyFor(consultaId);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const blob = JSON.parse(raw) as DraftBlob;
    const now = Date.now();
    const valid =
      blob &&
      typeof blob === 'object' &&
      blob.v === SCHEMA_VERSION &&
      blob.ownerAuthUserId === ownerAuthUserId &&
      typeof blob.createdAt === 'number' &&
      typeof blob.savedAt === 'number' &&
      blob.savedAt <= now + 60_000 &&
      now - blob.savedAt < TTL_SLIDING_MS &&
      now - blob.createdAt < TTL_ABSOLUTE_MS;
    const fields = valid ? sanitizeFields(blob.fields) : null;
    const baseline = valid ? sanitizeFields(blob.baseline) : null;
    if (!fields || !baseline) {
      sessionStorage.removeItem(key);
      return null;
    }
    return { fields, baseline, ownerAuthUserId, createdAt: blob.createdAt, savedAt: blob.savedAt };
  } catch {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* noop */
    }
    return null;
  }
}

/** Devuelve el `savedAt` escrito, o -1 si el storage rechazó (quota / modo privado). */
export function saveDraft(
  consultaId: string,
  input: { fields: FichaFields; baseline: FichaFields; ownerAuthUserId: string; createdAt: number }
): number {
  const savedAt = Date.now();
  if (!isBrowser() || !consultaId || !input.ownerAuthUserId) return -1;
  const blob: DraftBlob = {
    v: SCHEMA_VERSION,
    fields: input.fields,
    baseline: input.baseline,
    ownerAuthUserId: input.ownerAuthUserId,
    createdAt: input.createdAt,
    savedAt,
  };
  try {
    sessionStorage.setItem(keyFor(consultaId), JSON.stringify(blob));
    return savedAt;
  } catch {
    return -1;
  }
}

export function clearDraft(consultaId: string): void {
  if (!isBrowser() || !consultaId) return;
  try {
    sessionStorage.removeItem(keyFor(consultaId));
  } catch {
    /* noop */
  }
}

/** Wipe de TODOS los borradores de ficha. Llamar en logout (equivale a SEC-043). */
export function clearAllFichaDrafts(): void {
  if (!isBrowser()) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(KEY_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* noop */
  }
}

const RTF =
  typeof Intl !== 'undefined' && 'RelativeTimeFormat' in Intl
    ? new Intl.RelativeTimeFormat('es', { numeric: 'auto' })
    : null;

export function relativeTimeFromNow(ts: number): string {
  const diffSec = Math.round((ts - Date.now()) / 1000);
  if (RTF) {
    if (Math.abs(diffSec) < 60) return RTF.format(diffSec, 'second');
    if (Math.abs(diffSec) < 3600) return RTF.format(Math.round(diffSec / 60), 'minute');
    return RTF.format(Math.round(diffSec / 3600), 'hour');
  }
  const s = Math.abs(diffSec);
  return s < 60 ? 'recién' : `hace ${Math.round(s / 60)} min`;
}
